import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ToastVariant = 'success' | 'warning' | 'error' | 'info';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
}

export interface ToastConfig {
  message: string;
  variant?: ToastVariant;
  duration?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$: Observable<Toast[]> = this.toastsSubject.asObservable();

  /**
   * Auto-dismiss defaults, in milliseconds.
   *
   * Both are non-zero on purpose: a toast the operator never asked to keep
   * takes itself down. The error default is the *longer* of the two — a
   * failure deserves more reading time than a routine notification, which is
   * the opposite of what these defaults used to say (F-232). A caller that
   * genuinely needs a toast to stay until it is closed by hand passes an
   * explicit `0`; that still means "no auto-dismiss".
   */
  private defaultDuration = 5000;
  private errorDuration = 8000;

  /**
   * How many toasts may be on screen at once. A burst of realtime events can
   * outrun any duration, so the stack is capped and the oldest toast is
   * dropped to make room — including one a caller pinned open with `0`, which
   * is the price of never burying the page under notifications.
   */
  private maxVisible = 5;

  /**
   * Show a toast notification
   */
  show(config: ToastConfig): string {
    const id = this.generateId();
    const variant: ToastVariant = config.variant ?? 'info';
    const duration =
      config.duration ??
      (variant === 'error' ? this.errorDuration : this.defaultDuration);
    const toast: Toast = {
      id,
      message: config.message,
      variant,
      duration,
    };

    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next([...currentToasts, toast].slice(-this.maxVisible));

    // Auto-remove after duration
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => {
        this.remove(id);
      }, toast.duration);
    }

    return id;
  }

  /**
   * Show a success toast
   */
  success(message: string, duration?: number): string {
    return this.show({ message, variant: 'success', duration });
  }

  /**
   * Show a warning toast
   */
  warning(message: string, duration?: number): string {
    return this.show({ message, variant: 'warning', duration });
  }

  /**
   * Show an error toast
   */
  error(message: string, duration?: number): string {
    return this.show({ message, variant: 'error', duration });
  }

  /**
   * Show an info toast
   */
  info(message: string, duration?: number): string {
    return this.show({ message, variant: 'info', duration });
  }

  /**
   * Remove a specific toast by ID
   */
  remove(id: string): void {
    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next(currentToasts.filter(toast => toast.id !== id));
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.toastsSubject.next([]);
  }

  /**
   * Generate a unique ID for each toast
   */
  private generateId(): string {
    return `toast_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
}
